import React, { useState } from "react";
import { useUser } from "../context/user-context";
import Register from "./Register";
import { logo, smallLogo } from "../assets/images";
import { AiOutlineEyeInvisible, AiOutlineEye } from "react-icons/ai";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/auth-context";
import { API } from "../constants/api";
import { getDeviceInfo } from "../utils/deviceInfo";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { errorMessage } from "../utils/errorMessage";
import useAuthAxios from "../utils/baseAxios";
import ClipLoader from "react-spinners/ClipLoader";
import { validateLoginDetails } from "../utils/validations";
import Oauth from "./Oauth";
const Login = () => {
  const { authenticate } = useAuth();
  const { theme } = useUser();
  const axiosInstance = useAuthAxios();
  const location = useLocation();
  const { state } = location;
  const { from = "/dashboard" } = state || {};
  const navigate = useNavigate();

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [viewPassword, setViewPassword] = useState<boolean>(false);

  const deviceInfo = getDeviceInfo();

  const signInMutation = useMutation({
    mutationFn: ({
      email,
      password,
      deviceInfo,
      rolePass,
    }: {
      email: string;
      password: string;
      deviceInfo: any;
      rolePass: string;
    }) =>
      axiosInstance.post(API.login, {
        email,
        password,
        deviceInfo: {
          name: deviceInfo.deviceName,
          uniqueId: deviceInfo.uniqueId,
          os: deviceInfo.operatingSystem?.family,
        },
        rolePass,
      }),
    onSuccess: (response: any) => {
      const data = response.data.data;
      // Branch on the 2FA marker, not on the presence of a token: under cookie auth the
      // session arrives as a Set-Cookie header, and `data.token` disappears entirely once
      // the backend stops returning it at the end of the migration.
      if (data?.status !== "2fa_required") {
        toast.success("You are logged in.");
        setTimeout(async () => {
          await authenticate();
          navigate(from);
        }, 2500);
        return;
      }
      const method = data.method;
      const state = { email, password, from };
      if (method === "email") {
        navigate("/2fa-auth", {
          state: { ...state, emailAuth: true, authApp: false },
        });
      } else if (method === "auth-app") {
        navigate("/2fa-auth-app", {
          state: { ...state, emailAuth: false, authApp: true },
        });
      } else if (method === "both") {
        navigate("/auth-select", {
          state: { ...state, emailAuth: true, authApp: true },
        });
      } else {
        toast.error("Unexpected 2FA response. Please try again.");
      }
    },
    onError: (error: any) => {
      toast.error(
        errorMessage((error?.data as any)?.message || String(error?.data))
      );
    },
  });

  const handleSubmit = () => {
    if (!validateLoginDetails(email, password)) return;
    signInMutation.mutate({
      email,
      password,
      deviceInfo,
      rolePass: "user",
    });
  };

  const labelClassName =
    "text-[13px] font-medium text-[#4E606E] dark:text-[#94A3B8]";
  const inputClassName =
    "h-[50px] w-full rounded-xl border border-gray-200 bg-gray-50 px-4 text-[13px] font-medium text-[#13181B] outline-none placeholder:text-gray-400 focus:border-secondary_button focus:ring-1 focus:ring-secondary_button dark:border-[#2A3441] dark:bg-[#1F2B36] dark:text-white dark:placeholder:text-[#6B7280]";

  return (
    <div className="w-full mds:w-4/5 md:w-3/5 xxxl:w-[50%] px-6 lgss:px-0 pt-28 pb-20 overflow-auto lgss:pt-16 font-manrope h-full mx-auto flex-col justify-between  items-center  flex">
      <div className="flex flex-col justify-center  items-center   w-full">
        <div>
          {theme === "dark" ? (
            <img src={logo} className="" alt="" />
          ) : (
            <img src={smallLogo} className="" alt="" />
          )}
        </div>
        <h2 className="text-text_black dark:text-white font-sora text-[24px] font-bold pt-4 lgss:pt-4">
          Log in to DeeXoptions
        </h2>
        <div className="w-full mt-8 lgss:w-2/3 xxxl:w-2/5 lg:w-[60%]">
          <Oauth />
        </div>
        <div className="mt-6 pt-8 w-full px-5 lgss:px-0 lgss:w-2/3 lg:w-[60%] xxxl:w-2/5">
          <div>
            <label className={labelClassName}>Email</label>
            <input
              type="email"
              value={email}
              placeholder="e.g. email@domain.com"
              onChange={(e) => setEmail(e.target.value.toLowerCase())}
              name="email"
              className={`mt-2 ${inputClassName}`}
            />
          </div>
          <div className="mt-6">
            <label className={labelClassName}>Password</label>
            <div className="relative mt-2">
              <input
                type={viewPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                name="password"
                className={`${inputClassName} pr-11`}
              />
              <button
                type="button"
                onClick={() => setViewPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4E606E] dark:text-[#94A3B8]"
                aria-label={viewPassword ? "Hide password" : "Show password"}
              >
                {!viewPassword ? (
                  <AiOutlineEyeInvisible className="text-xl" />
                ) : (
                  <AiOutlineEye className="text-xl" />
                )}
              </button>
            </div>
          </div>
          <button
            onClick={() => navigate("/forgot-password")}
            className=" mt-4 cursor-pointer   font-bold font-manrope text-[#0B84CB]"
          >
            Forgot password?
          </button>
        </div>
      </div>
      <div className="w-full mds:w-[90%] lgss:w-1/2 xxxl:w-2/5 lg:w-[60%] flex flex-col justify-center items-center">
        <h4 className="font-manrope dark:text-gray-300  text-text_black font-medium text-[12px] pt-6">
          Don’t have an account?{" "}
          <span
            onClick={() => {
              navigate("/signup");
            }}
            className="underline text-secondary_button tracking-wider cursor-pointer"
          >
            Sign Up
          </span>
        </h4>

        <button
          onClick={handleSubmit}
          disabled={signInMutation.isPending}
          className="w-full  h-[52px] flex justify-center items-center font-manrope font-medium rounded-xl text-white bg-secondary_button mt-8 lgss:mt-8"
        >
          {signInMutation.isPending ? (
            <ClipLoader color="#FFFFFF" size={30} />
          ) : (
            "Sign In"
          )}
        </button>
      </div>
    </div>
  );
};
const LoginPage = () => (
  <div className="lgss:flex lgss:flex-row h-screen dark:bg-[#13181B]">
    <Register />
    <Login />
  </div>
);

export default LoginPage;
